import {gql} from '@apollo/client';
import {
  BUILDER_DETAILS,
  GOAL,
  OV_POT_FRAGMENT,
  OV_RECOMMENDATION,
} from '../../../graphql/fragments';
import {PROTECTION_POT} from '../../potQueries';

export const LIST_OV_BUILDER = gql`
  ${OV_RECOMMENDATION}
  query OVBuilders($entityName: String!, $entityId: UUID!) {
    ovrecommends(entityName: $entityName, entityId: $entityId) {
      ...OVRecommendation
    }
  }
`;

export const DELETE_OV_BUILDER = gql`
  mutation DeleteOvbuilder($ovrecommendId: UUID!) {
    deleteOvrecommend(ovrecommendId: $ovrecommendId) {
      ok
      ovrecommendId
    }
  }
`;

export const OV_PROTECTION_BUILDER_DETAILS = gql`
  query OvProtectionbuilderDetails($ovrecommendId: UUID!) {
    ovrecommendDetails(ovrecommendId: $ovrecommendId) {
      id
      name
      dateUpdated
      dateCreated
      solutionType
      isXo
      isSppDaXo
      isPensionSharing
      isDependantsPension
      isAps
      selectedMandate
      goal {
        ...Goal
      }
      review {
        id
        dateSubmitted
      }
      contact {
        id
        simplifiedName
      }
      account {
        id
      }
      productProvider {
        id
        name
        isBank
        reportName
      }
      productName {
        id
        name
        reportName
      }
      recommends {
        id
        name
        curoId
        isFailedSubmission
      }
      pots {
        id
        fees {
          id
        }
        legacyInitialFee
        legacyInitialEmployerFee
        initialFee {
          id
        }
        initialEmployerFee {
          id
        }
        pot {
          ...ProtectionPot
        }
        adhocFeeType
        adhocFeeAmount
        adhocFeeDate
        adhocFeeReason
      }
      sourceOfFunds
      urgency
      urgencyReason
      isFinalised
      action
      isNewPlanRequired
      planType
      withdrawalReason
      withdrawalAmount
      startDate
      endDate
      note
    }
  }
  ${PROTECTION_POT}
  ${GOAL}
`;
export const OV_BUILDER_DETAILS = gql`
  ${BUILDER_DETAILS}
  query OvbuilderDetails($ovrecommendId: UUID!) {
    ovrecommendDetails(ovrecommendId: $ovrecommendId) {
      ...BuilderDetails
    }
  }
`;

export const OV_BUILDER_ASSOCIATED_ITEMS = gql`
  query OvbuilderAssociatedItems($ovrecommendId: UUID!) {
    ovrecommendDetails(ovrecommendId: $ovrecommendId) {
      pots {
        id
        pot {
          id
          isShellPlan
          servicingRights
          originalOvrId
          isDummy
          inTrust
          ovrecommendpots {
            ovrecommend {
              id
              name
              isDeleted
            }
          }
        }
      }
    }
  }
`;

export const UPSERT_BUILDER = gql`
  ${BUILDER_DETAILS}
  mutation UpsertOvbuilder(
    $ovrecommendId: UUID
    $reviewId: UUID
    $contactId: UUID
    $accountId: UUID
    $goalId: UUID
    $productProviderId: UUID
    $productNameId: UUID
    $name: String
    $action: OVRecommendActionEnum!
    $isNewPlanRequired: Boolean
    $planType: String
    $solutionType: String
    $isXo: Boolean
    $withdrawalReason: String
    # $withdrawalAmount: Decimal # not used, instead calculated
    $urgency: String
    $urgencyReason: String
    $startDate: DateTime
    $endDate: DateTime
    $note: String
    $isSppDaXo: Boolean
    $isAps: Boolean
    $isDependantsPension: Boolean
    $isPensionSharing: Boolean
    $parentGuardianId: UUID
  ) {
    upsertOvrecommend(
      ovrecommendId: $ovrecommendId
      reviewId: $reviewId
      contactId: $contactId
      accountId: $accountId
      goalId: $goalId
      productProviderId: $productProviderId
      productNameId: $productNameId
      name: $name
      action: $action
      isNewPlanRequired: $isNewPlanRequired
      planType: $planType
      solutionType: $solutionType
      isXo: $isXo
      withdrawalReason: $withdrawalReason
      # withdrawalAmount: $withdrawalAmount # not used, instead calculated
      urgency: $urgency
      urgencyReason: $urgencyReason
      startDate: $startDate
      endDate: $endDate
      note: $note
      isSppDaXo: $isSppDaXo
      isAps: $isAps
      isDependantsPension: $isDependantsPension
      isPensionSharing: $isPensionSharing
      parentGuardianId: $parentGuardianId
      isDentist: true
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const GENERATE_BUILDER_POT = gql`
  mutation GenerateOvbuilderPots(
    $ovrecommendId: UUID!
    $accountId: UUID!
    $args: [GenerateOVRPotBatchArguments!]!
  ) {
    generateOvrpotBatch(
      args: $args
      ovrecommendId: $ovrecommendId
      accountId: $accountId
    ) {
      ovrecommendpots {
        ...OVPot
      }
      errors
    }
  }
  ${OV_POT_FRAGMENT}
`;

export const UPSERT_BUILDER_MODIFY = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderModify(
    $ovrecommendId: UUID!
    $potId: UUID!
    $feeIds: [UUID!]
    $modelPortfolioId: UUID
    $nonSaltusMandate: String
  ) {
    upsertModify(
      ovrecommendId: $ovrecommendId
      potId: $potId
      feeIds: $feeIds
      modelPortfolioId: $modelPortfolioId
      nonSaltusMandate: $nonSaltusMandate
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_PROTECTION = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderProtection(
    $ovrecommendId: UUID!
    $protectionType: ProtectionTypeEnum!
    $benefitBasis: String
    $benefitBasisMonthlyText: String
    $criticalIllnessCover: Decimal
    $deferredPeriod: String
    $isInTrust: Boolean
    $indexation: String
    $isInvestmentLinked: Boolean
    $lifeCover: Decimal
    $lifeOrCriticalIllnessCover: Decimal
    $livesAssured: String
    $monthlyIncome: Decimal
    $notes: String
    $premiumType: String
    $term: Int
    $termType: String
    $terminatingAge: Int
    $isWaiverOfPremium: Boolean
  ) {
    upsertProtection(
      ovrecommendId: $ovrecommendId
      protectionType: $protectionType
      benefitBasis: $benefitBasis
      benefitBasisMonthlyText: $benefitBasisMonthlyText
      criticalIllnessCover: $criticalIllnessCover
      deferredPeriod: $deferredPeriod
      isInTrust: $isInTrust
      indexation: $indexation
      isInvestmentLinked: $isInvestmentLinked
      lifeCover: $lifeCover
      lifeOrCriticalIllnessCover: $lifeOrCriticalIllnessCover
      livesAssured: $livesAssured
      monthlyIncome: $monthlyIncome
      notes: $notes
      premiumType: $premiumType
      term: $term
      termType: $termType
      terminatingAge: $terminatingAge
      isWaiverOfPremium: $isWaiverOfPremium
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_ANNUITY = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderAnnuity(
    $ovrecommendId: UUID!
    $potId: UUID!
    $annuityType: AnnuityTypeEnum!
    $isFull: Boolean!
    $guaranteedPeriod: Decimal!
    $incomeFrequency: AnnuityIncomeFrequencyEnum!
    $isIndexLinked: Boolean!
    $initialFeeId: UUID
    $sppInitialFee: String
    $isLtcCapitalProtected: Boolean!
    $notes: String!
    $paymentType: AnnuityPaymentTypeEnum!
    $protectedPercentage: Decimal!
    $rateType: AnnuityRateTypeEnum!
    $singleClientAmount: Decimal!
    $spouseAnnuityPercent: Decimal!
  ) {
    upsertAnnuity(
      ovrecommendId: $ovrecommendId
      potId: $potId
      annuityType: $annuityType
      isFull: $isFull
      guaranteedPeriod: $guaranteedPeriod
      incomeFrequency: $incomeFrequency
      isIndexLinked: $isIndexLinked
      initialFeeId: $initialFeeId
      sppInitialFee: $sppInitialFee
      isLtcCapitalProtected: $isLtcCapitalProtected
      notes: $notes
      paymentType: $paymentType
      protectedPercentage: $protectedPercentage
      rateType: $rateType
      singleClientAmount: $singleClientAmount
      spouseAnnuityPercent: $spouseAnnuityPercent
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_CONTRIBUTE = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderContribute(
    $ovrecommendId: UUID!
    $destinationPotId: UUID!
    $isFull: Boolean!
    $isInSpecie: Boolean!
    $isNewCashContribution: Boolean!
    $sourcePotId: UUID
    $comboFrequency: ComboFrequencyEnum!
    $singleClientAmount: Decimal!
    $recurringClientAmount: Decimal!
    $singleEmployerAmount: Decimal!
    $recurringEmployerAmount: Decimal!
    $initialFeeId: UUID
    $initialEmployerFeeId: UUID
    $sppInitialFee: String
    $sppInitialEmployerFee: String
  ) {
    upsertContribute(
      ovrecommendId: $ovrecommendId
      destinationPotId: $destinationPotId
      isFull: $isFull
      isInSpecie: $isInSpecie
      isNewCashContribution: $isNewCashContribution
      sourcePotId: $sourcePotId
      comboFrequency: $comboFrequency
      singleClientAmount: $singleClientAmount
      recurringClientAmount: $recurringClientAmount
      singleEmployerAmount: $singleEmployerAmount
      recurringEmployerAmount: $recurringEmployerAmount
      initialFeeId: $initialFeeId
      initialEmployerFeeId: $initialEmployerFeeId
      sppInitialFee: $sppInitialFee
      sppInitialEmployerFee: $sppInitialEmployerFee
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_WITHDRAW = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderWithdraw(
    $ovrecommendId: UUID!
    $sourcePotId: UUID!
    $destinationPotId: UUID
    $isFull: Boolean!
    $isWithdrawToNewBank: Boolean!
    $withdrawalFrequency: RecurringFrequencyEnum!
    $withdrawalAmount: Decimal!
    $productProviderId: UUID
    $contactId: UUID
    $planNumber: String
    $sortCode: String
    $policyHolder: String
    $bankDetails: BankDetailsEnum
  ) {
    upsertWithdraw(
      ovrecommendId: $ovrecommendId
      sourcePotId: $sourcePotId
      destinationPotId: $destinationPotId
      isFull: $isFull
      isWithdrawToNewBank: $isWithdrawToNewBank
      withdrawalFrequency: $withdrawalFrequency
      withdrawalAmount: $withdrawalAmount
      productProviderId: $productProviderId
      contactId: $contactId
      planNumber: $planNumber
      sortCode: $sortCode
      policyHolder: $policyHolder
      bankDetails: $bankDetails
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_TRANSFER = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderTransfer(
    $ovrecommendId: UUID!
    $destinationPotId: UUID!
    $existingSources: [ExistingTransferSourceInputType]!
    $newSources: [NewTransferSourceInputType]!
  ) {
    upsertTransfer(
      ovrecommendId: $ovrecommendId
      destinationPotId: $destinationPotId
      existingSources: $existingSources
      newSources: $newSources
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_ACTION = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderAction($ovrecommendId: UUID!) {
    upsertAction(ovrecommendId: $ovrecommendId) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_SET_UP_NEW = gql`
  ${BUILDER_DETAILS}
  mutation UpsertSetUpNew(
    $ovrecommendId: UUID!
    $productProviderId: UUID!
    $productNameId: UUID!
    $subplans: [SubplansInputType]!
  ) {
    upsertSetUpNew(
      ovrecommendId: $ovrecommendId
      productProviderId: $productProviderId
      productNameId: $productNameId
      subplans: $subplans
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;

export const UPSERT_BUILDER_CHARGE_AD_HOC_FEE = gql`
  ${BUILDER_DETAILS}
  mutation UpsertBuilderChargeAdHocFee(
    $ovrecommendId: UUID!
    $potId: UUID!
    $adhocFeeAmount: Decimal!
    $adhocFeeType: AdhocFeeTypeEnum!
    $adhocFeeReason: String!
  ) {
    upsertChargeAdHocFee(
      ovrecommendId: $ovrecommendId
      potId: $potId
      adhocFeeAmount: $adhocFeeAmount
      adhocFeeType: $adhocFeeType
      adhocFeeReason: $adhocFeeReason
    ) {
      ok
      errors
      ovrecommend {
        ...BuilderDetails
      }
    }
  }
`;
